import React, { useRef } from 'react'
import { CButton, CFormInput, CFormLabel } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import JSZip from 'jszip'
import { icon } from '../../../shared/icons'
import { useBostonState, emit } from '../lib/useBoston'
import { state, Storage, uuidV4 } from '../lib/store'
import { csReindexTags } from '../lib/builder'
import { useUI } from '../../../shared/ui/UIProvider'
import { displayRemarkType, normalizeRemarkField } from '../lib/remarkSemantics'

/* Port fiel de viewEditor/bindEditor + edApplyScript de boston_ia:
   cargar un Custom Script ya generado (desde la biblioteca local) y editarlo
   en el Custom Script Builder. edApplyScript tambien lo usan el Importador IA
   y la Revision Dev para volcar un data.json al builder. */

/* Aplica un data.json al Custom Script Builder (logica VERBATIM del original).
   NO muestra avisos: el caller decide el toast (el alert original vivia aca). */
function parseMaybeJSON(value, fallback = {}) {
  if (!value) return fallback
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

export function edApplyScript(data, config, scriptName, appJs, fichaMd, logicJson, options = {}) {
  // FIX preservacion: guardar el data.json COMPLETO original. El builder edita su vista
  // simplificada, pero csBuildDataJson() restaura al guardar todas las claves que el
  // builder no conoce (onLoad, onlyWrite, enableMetrics, viewCatalogues, etc.).
  try {
    state.cs.raw = JSON.parse(JSON.stringify(data))
    if (state.cs.raw && state.cs.raw._uuid !== undefined) delete state.cs.raw._uuid
  } catch (e) {
    state.cs.raw = null
  }
  state.cs.name = scriptName || data.name || 'script_editado'
  state.cs.title = data.title || scriptName
  state.cs.validateActiveReservation = data.validateActiveReservation !== false
  state.cs.sendCommands = !!data.sendCommands
  state.cs.orderExecute = data.orderExecute !== false
  state.cs.editCatalogues = !!data.editCatalogues
  state.cs.reintentos = data.reintentos || 0
  state.cs.targetEnv = data.targetEnv || 'stg'
  state.cs.forms =
    Array.isArray(data.forms) && data.forms.length
      ? data.forms.map((f) => ({
          ...f,
          form: f.form || 'form1',
          title: f.title || 'FORM',
          title_visible: f.title_visible !== false,
          remarks: (f.remarks || []).map((r) => ({
            // FIX preservacion: conservar TODAS las claves del remark original
            // (skip, visible, coincidence, group, dato, block, index, data_test, ...)
            ...normalizeRemarkField(r),
            campoForm: r.campoForm || '',
            label: r.label || '',
            mandatory: !!r.mandatory,
            remark: r.remark || '',
            remark_mask: r.remark_mask || '',
            source_additional: r.source_additional || '',
            typedata: r.typedata || 'text',
            typeremark: displayRemarkType(r),
            datasource: r.datasource || [],
          })),
        }))
      : [{ form: 'form1', title: 'FORMULARIO', title_visible: true, remarks: [] }]
  state.cs.postCommands = data.postCommands || []
  state.cs.logicJson = logicJson || null
  // AGENTE 4: asociar el handler (si viene) para que Guardar+Publicar lo incluya como app.js
  state.cs.handlerJs = appJs || null
  // FICHA.md asociada (documentacion del script — se publica junto a los archivos)
  state.cs.fichaMd = fichaMd || null
  state.cs.uuid = (config && config.config && config.config.uuid) || state.cs.uuid || uuidV4()
  state.cs.url = options.url || ''
  state.cs.config = parseMaybeJSON(options.customScriptConfig, {})
  state.cs.persisted = options.persisted === true || options.isSaved === true || !!state.cs.url
  state.cs.isSaved = state.cs.persisted
  csReindexTags()
  // "Saltar al builder": el shell (BostonIAPage) usa estado local de vista, asi que
  // mutamos state.view y emitimos; el caller avisa con un toast que abra la pestaña Custom Script.
  if (!options.stayInCurrentView) state.view = 'custom'
  emit()
  return { uuid: state.cs.uuid, forms: state.cs.forms.length, commands: state.cs.postCommands.length }
}

function EditorView() {
  const state = useBostonState()
  const { toast, confirm } = useUI()
  const libCount = state.library.length
  const customScripts = state.library.filter((s) => s.type === 'custom-script')
  const dataFileRef = useRef(null)
  const configFileRef = useRef(null)
  const zipFileRef = useRef(null)

  function loadFromLibrary(item) {
    const info = edApplyScript(item.dataJson, item.configJson, item.name, item.appJs || null, item.fichaMd || null, item.logicJson || null, {
      url: item.url || '',
      customScriptConfig: item.config || item.customScriptConfig || null,
      persisted: item.persisted || item.isSaved,
    })
    toast(
      `Script "${item.name}" cargado en el builder — abrí la pestaña Custom Script. UUID: ${info.uuid} · Forms: ${info.forms} · Commands: ${info.commands}. Editá lo que necesites y dale "Guardar local".`,
      'success',
      { title: 'Script cargado en el builder' },
    )
  }

  async function deleteFromLibrary(item) {
    const ok = await confirm({
      title: 'Borrar de la biblioteca',
      body: (
        <span style={{ whiteSpace: 'pre-line' }}>
          {`¿Borrar el script "${item.name}" de la biblioteca?\n\nUUID: ${item.uuid}\n\n(NO borra los archivos del disco; solo el registro local.)`}
        </span>
      ),
      confirmText: 'Borrar',
      cancelText: 'Cancelar',
      tone: 'danger',
    })
    if (!ok) return
    state.library = state.library.filter((x) => x.id !== item.id)
    Storage.saveLibrary(state.library)
    emit()
  }


  /* --- Carga desde archivo: los otros dos caminos del original (data.json / .zip) --- */
  async function loadFromFiles() {
    const dataFile = dataFileRef.current && dataFileRef.current.files && dataFileRef.current.files[0]
    if (!dataFile) {
      toast('Elegi al menos el data.json.', 'warning')
      return
    }
    try {
      const data = JSON.parse(await dataFile.text())
      let config = null
      const configFile = configFileRef.current && configFileRef.current.files && configFileRef.current.files[0]
      if (configFile) {
        try {
          config = JSON.parse(await configFile.text())
        } catch (e) {
          toast('config_app.json invalido — se ignora (se mantiene/genera UUID).', 'warning')
        }
      }
      const name = (data.title || dataFile.name.replace(/\.json$/i, '')).toLowerCase().replace(/[^a-z0-9-_]+/g, '_')
      const info = edApplyScript(data, config, name)
      toast(
        `"${name}" cargado en el builder — abri la pestaña Custom Script. UUID: ${info.uuid} · Forms: ${info.forms} · Commands: ${info.commands}.`,
        'success',
        { title: 'Script cargado desde archivo' },
      )
    } catch (e) {
      toast('data.json invalido: ' + e.message, 'danger')
    }
  }

  async function loadFromZip() {
    const zf = zipFileRef.current && zipFileRef.current.files && zipFileRef.current.files[0]
    if (!zf) {
      toast('Elegi un .zip de script.', 'warning')
      return
    }
    try {
      const zip = await JSZip.loadAsync(zf)
      const dataEntry = zip.file(/(^|\/)data\.json$/i)[0]
      if (!dataEntry) throw new Error('el .zip no contiene data.json')
      const data = JSON.parse(await dataEntry.async('string'))
      let config = null
      const cfgEntry = zip.file(/(^|\/)config_app\.json$/i)[0]
      if (cfgEntry) {
        try {
          config = JSON.parse(await cfgEntry.async('string'))
        } catch (e) {
          /* config invalido en zip: se ignora */
        }
      }
      const name = (data.title || zf.name.replace(/\.zip$/i, '')).toLowerCase().replace(/[^a-z0-9-_]+/g, '_')
      const info = edApplyScript(data, config, name)
      toast(
        `"${name}" cargado desde el .zip — abri la pestaña Custom Script. UUID: ${info.uuid} · Forms: ${info.forms} · Commands: ${info.commands}.`,
        'success',
        { title: 'Script cargado desde .zip' },
      )
    } catch (e) {
      toast('No se pudo leer el .zip: ' + e.message, 'danger')
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Editar script existente</h1>
          <p>
            Carga un Custom Script ya generado y editalo. Tres caminos: subir el <code>.zip</code>, subir el{' '}
            <code>data.json</code> + <code>config_app.json</code>, o elegir uno de la biblioteca local.
          </p>
        </div>
      </div>

      <div className="card-x">
        <h3>📤 Cargar desde archivo</h3>
        <div className="bia-grid-2">
          <div>
            <CFormLabel>
              <code>data.json</code> del script (obligatorio)
            </CFormLabel>
            <CFormInput type="file" accept=".json,application/json" ref={dataFileRef} size="sm" />
          </div>
          <div>
            <CFormLabel>
              <code>config_app.json</code> (opcional — conserva el UUID original)
            </CFormLabel>
            <CFormInput type="file" accept=".json,application/json" ref={configFileRef} size="sm" />
          </div>
        </div>
        <div className="bia-row-input" style={{ marginTop: 10 }}>
          <CButton color="success" className="btn-brand" onClick={loadFromFiles}>
            <CIcon icon={icon('cilCloudUpload')} /> Cargar al builder
          </CButton>
          <span style={{ flex: 1 }} />
          <CFormInput type="file" accept=".zip" ref={zipFileRef} size="sm" style={{ maxWidth: 260 }} />
          <CButton color="secondary" variant="outline" onClick={loadFromZip}>
            <CIcon icon={icon('cilFolderOpen')} /> Cargar .zip
          </CButton>
        </div>
      </div>

      <div className="card-x">
        <div className="bia-card-head">
          <h3>
            Scripts en biblioteca local
          </h3>
          <span className="bia-pill">{libCount}</span>
        </div>
        {customScripts.length === 0 ? (
          <p className="bia-empty">Sin scripts en la biblioteca. Crea uno desde el Custom Script Builder primero.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
            {customScripts.map((s) => (
              <div key={s.id} style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
                <CButton
                  color="secondary"
                  variant="outline"
                  onClick={() => loadFromLibrary(s)}
                  style={{ flex: 1, textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>
                    <strong>{s.name}</strong>
                    <br />
                    <small style={{ color: 'var(--muted)' }}>{s.title || ''}</small>
                  </span>
                  <small className="bia-mono" style={{ color: 'var(--muted)' }}>
                    {(s.uuid || '').slice(0, 8)}
                  </small>
                </CButton>
                <CButton
                  color="danger"
                  variant="outline"
                  title="Borrar de la biblioteca"
                  style={{ width: 36, flexShrink: 0, padding: 0 }}
                  onClick={() => deleteFromLibrary(s)}
                >
                  <CIcon icon={icon('cilTrash')} />
                </CButton>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NOTA: el original tenia un card #ed-status "Vista previa del script cargado"
          SIEMPRE hidden (bindEditor nunca lo mostraba) — UI muerta, no se portea. */}
    </>
  )
}

export default EditorView
